import React, { useState, useEffect } from 'react';
import { 
  Search, CheckCircle, XCircle, Edit, Trash2, Plus, 
  ChevronRight, ChevronDown, Filter, SortAsc, Eye, 
  BookOpen, HelpCircle, List, Save, X, AlertCircle,
  MessageSquare, GripVertical,
  SquareCheck,
  Star
} from 'lucide-react';
import { 
  useQuery, 
  useMutation, 
  useQueryClient, 
} from '@tanstack/react-query';
import { supabase } from '@/src/supabase/client';
import { Database } from '@/database.types';

import "./Passages/Page.css";
import TextEditor from '@/src/components/Editor';
import { replaceTaggedValues } from '@/src/utils';

// Define types based on the SQL schema
type Passage = Database["public"]["Tables"]["passages"]["Row"];

type Question = {
  id: string;
  passage_id: string;
  answer_id: string | null;
  prompt: string;
  explanation: string | null;
  hint: string | null;
  level: string | null;
  approved: boolean | null;
};

type Answer = {
  id: string;
  question_id: string;
  sorted: number | null;
  content: string;
  approved: boolean | null;
};

const AdminPassagesPage = () => {
  return (
    <AdminPanelContent />
  );
};


const AdminPanelContent = () => {
  const [dragOverAnswerId, setDragOverAnswerId] = useState<string | null>(null);
  const [dragIndicatorPosition, setDragIndicatorPosition] = useState({ x: 0, y: 0 });
  const [touchStartY, setTouchStartY] = useState(null);
  const [touchedAnswerId, setTouchedAnswerId] = useState(null);

  // State for tracking selected items
  const [selectedPassage, setSelectedPassage] = useState<Passage | null>(null);
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
  
  // State for editing
  const [editingPassage, setEditingPassage] = useState<Passage | null>(null);
  const [emptyPassage, setEmptyPassage] = useState<Passage | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [emptyQuestion, setEmptyQuestion] = useState<Question | null>(null);
  const [editingAnswer, setEditingAnswer] = useState<Answer | null>(null);
  const [emptyAnswer, setEmptyAnswer] = useState<Answer | null>(null);
  
  // State for creating new items
  const [creatingPassage, setCreatingPassage] = useState(false);
  const [creatingQuestion, setCreatingQuestion] = useState(false);
  const [creatingAnswer, setCreatingAnswer] = useState(false);
  
  // Search and filter state
  const [passageSearch, setPassageSearch] = useState('');
  const [passageFilter, setPassageFilter] = useState<string | null>(null);
  const [questionSearch, setQuestionSearch] = useState('');
  const [answerSearch, setAnswerSearch] = useState('');
  const [approvalFilter, setApprovalFilter] = useState<boolean | null>(false);
  
  // Drag and drop state for answers
  const [draggedAnswerId, setDraggedAnswerId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  
  // Get the query client
  const queryClient = useQueryClient();

  useEffect(() => {
    if (selectedPassage) {
      setSelectedQuestion(null);
    }
  }, [selectedPassage]);

  const DragIndicator = ({ visible, position }) => {
    if (!visible) return null;
    
    return (
      <div 
        className="fixed pointer-events-none z-50 bg-indigo-600 text-white px-3 py-1 rounded-md shadow-lg"
        style={{
          left: position.x + 15,
          top: position.y + 15,
          opacity: 0.8,
          transform: 'translate(-50%, -50%)',
          transition: 'opacity 0.2s ease-out'
        }}
      >
        Moving Answer
      </div>
    );
  };
  
  // Add this to your mouse/touch move handlers
  const updateDragIndicatorPosition = (e) => {
    setDragIndicatorPosition({
      x: e.clientX || (e.touches && e.touches[0].clientX) || 0,
      y: e.clientY || (e.touches && e.touches[0].clientY) || 0
    });
  };


  // Fetch passages
  const { 
    data: passages = [], 
    isLoading: passagesLoading,
    error: passagesError
  } = useQuery({
    queryKey: ['passages', passageSearch, passageFilter, approvalFilter],
    queryFn: async () => {
      let query = supabase
        .from('passages')
        .select('*').order('created_at', {
          ascending: false,
        });
      
      if (passageSearch) {
        query = query.ilike('prompt', `%${passageSearch}%`);
      }
      
      if (passageFilter) {
        query = query.eq('question_type', passageFilter);
      }
      
      if (approvalFilter !== null) {
        query = approvalFilter === false ? query.or(`approved.is.null,approved.eq.false`) : query.eq('approved', approvalFilter);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      return data as Passage[];
    }
  });
  
  // Fetch questions for selected passage
  const { 
    data: questions = [], 
    isLoading: questionsLoading,
    error: questionsError
  } = useQuery({
    queryKey: ['questions', selectedPassage?.id, questionSearch, approvalFilter],
    queryFn: async () => {
      if (!selectedPassage) return [];
      
      let query = supabase
        .from('questions')
        .select('*')
        .eq('passage_id', selectedPassage.id);
      
      if (questionSearch) {
        query = query.ilike('prompt', `%${questionSearch}%`);
      }
      
      if (approvalFilter !== null) {
        query = approvalFilter === false ? query.or(`approved.is.null,approved.eq.false`) : query.eq('approved', approvalFilter);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      return data as Question[];
    },
    enabled: !!selectedPassage
  });
  
  // Fetch answers for selected question
  const { 
    data: answers = [], 
    isLoading: answersLoading,
    error: answersError
  } = useQuery({
    queryKey: ['answers', selectedQuestion?.id, answerSearch, approvalFilter],
    queryFn: async () => {
      if (!selectedQuestion) return [];
      
      let query = supabase
        .from('answers')
        .select('*')
        .eq('question_id', selectedQuestion.id);
      
      if (answerSearch) {
        query = query.ilike('content', `%${answerSearch}%`);
      }
      
      if (approvalFilter !== null) {
        query = approvalFilter === false ? query.or(`approved.is.null,approved.eq.false`) : query.eq('approved', approvalFilter);
      }
      
      query = query.order('sorted');
      
      const { data, error } = await query;
      
      if (error) throw error;
      return data as Answer[];
    },
    enabled: !!selectedQuestion
  });
  
  // Mutations for CRUD operations
  const createPassageMutation = useMutation({
    mutationFn: async (newPassage: Omit<Passage, 'id'>) => {
      const { data, error } = await supabase
        .from('passages')
        .insert(newPassage)
        .select()
        .single();
      
      if (error) throw error;
      return data as Passage;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['passages'] });
      setCreatingPassage(false);
    }
  });
  
  const updatePassageMutation = useMutation({
    mutationFn: async (passage: Passage) => {
      const { data, error } = await supabase
        .from('passages')
        .update({
          question_type: passage.question_type,
          prompt: passage.prompt,
          level: passage.level,
          approved: passage.approved
        })
        .eq('id', passage.id)
        .select()
        .single();
      
      if (error) throw error;
      return data as Passage;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['passages'] });
      if (selectedPassage?.id === data.id) {
        setSelectedPassage(data);
      }
      setEditingPassage(null);
    }
  });
  
  const deletePassageMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('passages')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return id;
    },
    onSuccess: (id) => {
      queryClient.invalidateQueries({ queryKey: ['passages'] });
      if (selectedPassage?.id === id) {
        setSelectedPassage(null);
        setSelectedQuestion(null);
      }
    }
  });
  
  const createQuestionMutation = useMutation({
    mutationFn: async (newQuestion: Omit<Question, 'id'>) => {
      const { data, error } = await supabase
        .from('questions')
        .insert(newQuestion)
        .select()
        .single();
      
      if (error) throw error;
      return data as Question;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['questions', selectedPassage?.id] });
      setCreatingQuestion(false);
    }
  });
  
  const updateQuestionMutation = useMutation({
    mutationFn: async (question: Question) => {
      const { data, error } = await supabase
        .from('questions')
        .update({
          prompt: question.prompt,
          explanation: question.explanation,
          hint: question.hint,
          level: question.level,
          answer_id: question.answer_id,
          approved: question?.approved
        })
        .eq('id', question.id)
        .select()
        .single();
      
      if (error) throw error;
      return data as Question;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['questions', selectedPassage?.id] });
      if (selectedQuestion?.id === data.id) {
        setSelectedQuestion(data);
      }
      setEditingQuestion(null);
    }
  });
  
  const deleteQuestionMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('questions')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return id;
    },
    onSuccess: (id) => {
      queryClient.invalidateQueries({ queryKey: ['questions', selectedPassage?.id] });
      if (selectedQuestion?.id === id) {
        setSelectedQuestion(null);
      }
    }
  });
  
  const createAnswerMutation = useMutation({
    mutationFn: async (newAnswer: Omit<Answer, 'id'>) => {
      const { data, error } = await supabase
        .from('answers')
        .insert(newAnswer)
        .select()
        .single();
      
      if (error) throw error;
      return data as Answer;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['answers', selectedQuestion?.id] });
      setCreatingAnswer(false);
      
      // If this is the first answer, set it as the correct answer
      if (selectedQuestion && !selectedQuestion.answer_id) {
        updateQuestionMutation.mutate({
          ...selectedQuestion,
          answer_id: data.id
        });
      }
    }
  });
  
  const updateAnswerMutation = useMutation({
    mutationFn: async (answer: Answer) => {
      const { data, error } = await supabase
        .from('answers')
        .update({
          content: answer.content,
          sorted: answer.sorted,
          approved: answer.approved
        })
        .eq('id', answer.id)
        .select()
        .single();
      
      if (error) throw error;
      return data as Answer;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['answers', selectedQuestion?.id] });
      setEditingAnswer(null);
    }
  });
  
  const deleteAnswerMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('answers')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return id;
    },
    onSuccess: (id) => {
      queryClient.invalidateQueries({ queryKey: ['answers', selectedQuestion?.id] });
      
      // If this was the correct answer, update the question
      if (selectedQuestion?.answer_id === id) {
        updateQuestionMutation.mutate({
          ...selectedQuestion,
          answer_id: null
        });
      }
    }
  });

  // Batch update answers' sorted field
  const updateAnswersSortOrderMutation = useMutation({
    mutationFn: async (updatedAnswers) => {
      // Create optimistic UI update
      const previousAnswers = [...answers];
      
      // Apply the updates to the local state first for a smoother experience
      queryClient.setQueryData(['answers', selectedQuestion?.id], (oldData) => {
        if (!oldData) return oldData;
        
        // Create a new array with the updated sort values
        return oldData.map(answer => {
          const update = updatedAnswers.find(u => u.id === answer.id);
          if (update) {
            return { ...answer, sorted: update.sorted };
          }
          return answer;
        }).sort((a, b) => (a.sorted || 0) - (b.sorted || 0));
      });
      
      try {
        // Send the actual API request
        const { data, error } = await supabase
          .from('answers')
          .upsert(updatedAnswers)
          .select();
        
        if (error) throw error;
        return data;
      } catch (error) {
        // If there's an error, revert to previous state
        queryClient.setQueryData(['answers', selectedQuestion?.id], previousAnswers);
        throw error;
      }
    },
    onSuccess: () => {
      // Refetch to ensure everything is in sync
      queryClient.invalidateQueries({ queryKey: ['answers', selectedQuestion?.id] });
    },
    onError: (error) => {
      console.error('Error updating answer sort order:', error);
      // Show an error toast or notification here
    }
  });
  
  // Handle selection changes
  useEffect(() => {
    if (selectedPassage === null) {
      setSelectedQuestion(null);
    }
  }, [selectedPassage]);
  
  // Reset form states when selection changes
  useEffect(() => {
    setEditingPassage(null);
    setEditingQuestion(null);
    setEditingAnswer(null);
    setCreatingPassage(false);
    setCreatingQuestion(false);
    setCreatingAnswer(false);
  }, [selectedPassage, selectedQuestion]);
  
  // Get unique question types for filtering
  const questionTypes = [...new Set(passages.map(p => p.question_type))];

  // Handle answer drag start
  const handleAnswerDragStart = (e, answerId) => {
    setDraggedAnswerId(answerId);
    setIsDragging(true);
    
    // Set initial indicator position
    setDragIndicatorPosition({
      x: e.clientX || (e.touches && e.touches[0].clientX) || 0,
      y: e.clientY || (e.touches && e.touches[0].clientY) || 0
    });

    // Set drag image and effect
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      
      // Optional: Create and set a custom drag image
      const dragImage = document.createElement('div');
      dragImage.textContent = 'Moving Answer';
      dragImage.style.padding = '8px 12px';
      dragImage.style.background = 'rgba(99, 102, 241, 0.8)';
      dragImage.style.color = 'white';
      dragImage.style.borderRadius = '4px';
      dragImage.style.position = 'absolute';
      dragImage.style.top = '-1000px';
      document.body.appendChild(dragImage);
      
      e.dataTransfer.setDragImage(dragImage, 0, 0);
      
      // Clean up the temporary element after a short delay
      setTimeout(() => {
        document.body.removeChild(dragImage);
      }, 100);
    }
  };

  const handleAnswerDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverAnswerId(null);
  };

  // Handle answer drag over
  const handleAnswerDragOver = (e: React.DragEvent, targetAnswerId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedAnswerId || draggedAnswerId === targetAnswerId) return;
    
    setDragOverAnswerId(targetAnswerId);
    
    // Set the dropEffect to indicate a move operation
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'move';
    }
  };

  const handleAnswerDrop = (e, targetAnswerId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedAnswerId || draggedAnswerId === targetAnswerId) {
      setIsDragging(false);
      setDraggedAnswerId(null);
      setDragOverAnswerId(null);
      return;
    }
    
    const reorderedAnswers = [...answers];
    const draggedAnswerIndex = reorderedAnswers.findIndex(answer => answer.id === draggedAnswerId);
    const targetAnswerIndex = reorderedAnswers.findIndex(answer => answer.id === targetAnswerId);
    
    if (draggedAnswerIndex === -1 || targetAnswerIndex === -1) {
      setIsDragging(false);
      setDraggedAnswerId(null);
      setDragOverAnswerId(null);
      return;
    }
  
    // Remove dragged item from original position
    const [draggedItem] = reorderedAnswers.splice(draggedAnswerIndex, 1);
    
    // Insert at new position
    reorderedAnswers.splice(targetAnswerIndex, 0, draggedItem);
  
    // Update sorted values for all answers (1-based indexing)
    const updatedAnswers = reorderedAnswers.map((answer, index) => ({
      ...answer,
      sorted: index + 1
    }));
  
    // Save the new order to the database
    updateAnswersSortOrderMutation.mutate(updatedAnswers);
    
    // Reset drag state
    setIsDragging(false);
    setDraggedAnswerId(null);
    setDragOverAnswerId(null);
  };
  
  const handleDragEnd = (e) => {
    e.preventDefault();
    setIsDragging(false);
    setDraggedAnswerId(null);
    setDragOverAnswerId(null);
  };

  const handleTouchStart = (e, answerId) => {
    setTouchStartY(e.touches[0].clientY);
    setTouchedAnswerId(answerId);
  };
  
  const handleTouchMove = (e, answerId) => {
    if (!touchStartY || !touchedAnswerId) return;
    
    const touchY = e.touches[0].clientY;
    const deltaY = touchY - touchStartY;
    
    // Only start dragging if the user has moved their finger a significant distance
    if (Math.abs(deltaY) > 10 && !isDragging) {
      setIsDragging(true);
      setDraggedAnswerId(touchedAnswerId);
    }
    
    if (isDragging) {
      // Find the answer element under the touch point
      const elements = document.elementsFromPoint(
        e.touches[0].clientX,
        e.touches[0].clientY
      );
      
      // Find the first element that is an answer container
      const answerElement = elements.find(el => 
        el.getAttribute('data-answer-id') && 
        el.getAttribute('data-answer-id') !== touchedAnswerId
      );
      
      if (answerElement) {
        const targetId = answerElement.getAttribute('data-answer-id');
        setDragOverAnswerId(targetId);
      }
    }
  };
  
  const handleTouchEnd = (e) => {
    if (isDragging && draggedAnswerId && dragOverAnswerId) {
      // Perform the drop operation
      const reorderedAnswers = [...answers];
      const draggedAnswerIndex = reorderedAnswers.findIndex(answer => answer.id === draggedAnswerId);
      const targetAnswerIndex = reorderedAnswers.findIndex(answer => answer.id === dragOverAnswerId);
      
      if (draggedAnswerIndex !== -1 && targetAnswerIndex !== -1) {
        // Remove dragged item from original position
        const [draggedItem] = reorderedAnswers.splice(draggedAnswerIndex, 1);
        
        // Insert at new position
        reorderedAnswers.splice(targetAnswerIndex, 0, draggedItem);
  
        // Update sorted values for all answers
        const updatedAnswers = reorderedAnswers.map((answer, index) => ({
          id: answer.id,
          sorted: index + 1
        }));
  
        // Save the new order to the database
        updateAnswersSortOrderMutation.mutate(updatedAnswers);
      }
    }
    
    // Reset all touch-related state
    setTouchStartY(null);
    setTouchedAnswerId(null);
    setIsDragging(false);
    setDraggedAnswerId(null);
    setDragOverAnswerId(null);
  };

  return (
    <div className="min-h-screen bg-linear-to-br from-blue-50 to-indigo-50">
      {/* Header */}
      <div className="py-8 px-4 sm:px-6 lg:px-8 bg-linear-to-r from-indigo-600 to-purple-600">
        <div className="max-w-7xl mx-auto">
          <div className="md:flex md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">LSAT Content Administration</h1>
              <p className="text-indigo-100">
                Manage passages, questions, and answers for the LSAT preparation platform.
              </p>
            </div>
            <div className="mt-4 md:mt-0">
              <div className="flex space-x-4">
                <button 
                  onClick={() => setCreatingPassage(true)}
                  className="px-4 py-2 bg-white text-indigo-600 rounded-lg font-medium hover:bg-indigo-50 transition-colors shadow-md flex items-center"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  New Passage
                </button>
                <select
                  value={approvalFilter === null ? '' : approvalFilter ? 'approved' : 'unapproved'}
                  onChange={(e) => {
                    if (e.target.value === '') setApprovalFilter(null);
                    else setApprovalFilter(e.target.value === 'approved');
                  }}
                  className="px-4 py-2 bg-indigo-500/30 text-white rounded-lg font-medium hover:bg-indigo-500/40 transition-colors border border-indigo-300"
                >
                  <option value="">All Approval Status</option>
                  <option value="approved">Approved Only</option>
                  <option value="unapproved">Unapproved Only</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main content - Horizontal Layout */}
      <div className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Three-column layout */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-[calc(120vh)] md:h-[calc(100vh-16rem)] overflow-y-auto">
            {/* Passages Column */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col">
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-xl font-bold text-gray-800 flex items-center">
                    <BookOpen className="h-5 w-5 mr-2 text-indigo-600" />
                    Passages
                  </h2>
                </div>
                <div className="flex flex-col space-y-2">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Search passages..."
                      value={passageSearch}
                      onChange={(e) => setPassageSearch(e.target.value)}
                    />
                  </div>
                  <select
                    value={passageFilter || ''}
                    onChange={(e) => setPassageFilter(e.target.value || null)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    <option value="">All Types</option>
                    {questionTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              {/* Passages List - Scrollable */}
              <div className="overflow-y-auto flex-1">
                <div className="divide-y divide-gray-200">
                  {passagesLoading ? (
                    <div className="p-6 text-center text-gray-500">Loading passages...</div>
                  ) : passagesError ? (
                    <div className="p-6 text-center text-red-500">Error loading passages</div>
                  ) : passages.length === 0 ? (
                    <div className="p-6 text-center text-gray-500">
                      No passages found. Create a new passage to get started.
                    </div>
                  ) : (
                    passages.map((passage) => (
                      <div 
                        key={passage.id}
                        className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                          selectedPassage?.id === passage.id ? 'bg-indigo-50' : ''
                        }`}
                        onClick={() => {
                          if (selectedPassage?.id === passage.id) {
                            setSelectedPassage(null);
                          } else {
                            setSelectedPassage(passage);
                          }
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center mb-2">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mr-2 ${
                                passage.approved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {passage.approved ? 'Approved' : 'Pending'}
                              </span>
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                {passage.question_type}
                              </span>
                              {passage.level && (
                                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  {passage.level}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center">
                              <ChevronRight className={`h-5 w-5 mr-2 text-gray-400 transition-transform ${
                                selectedPassage?.id === passage.id ? 'transform rotate-90' : ''
                              }`} />
                              <h3 className="text-sm font-medium text-gray-900">
                                {passage.prompt.length > 50 && selectedPassage?.id !== passage.id
                                  ? replaceTaggedValues(passage.prompt.substring(0, 50) + '...') 
                                  : replaceTaggedValues(passage.prompt)}
                              </h3>
                            </div>
                          </div>
                          <div className="flex items-center space-x-1 ml-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingPassage({...passage});
                              }}
                              className="p-1 text-gray-500 hover:text-indigo-600 transition-colors"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            {/* <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (confirm('Are you sure you want to delete this passage? This will also delete all associated questions and answers.')) {
                                  deletePassageMutation.mutate(passage.id);
                                }
                              }}
                              className="p-1 text-gray-500 hover:text-red-600 transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button> */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                updatePassageMutation.mutate({
                                  ...passage,
                                  approved: !passage.approved
                                });
                              }}
                              className={`p-1 text-gray-500 transition-colors`}
                              // className={`p-1 ${
                              //   passage.approved 
                              //     ? 'text-green-500 hover:text-gray-500' 
                              //     : 'text-gray-500 hover:text-green-500'
                              // } transition-colors`}
                            >
                              {passage.approved ? (
                                <XCircle className="h-4 w-4" />
                              ) : (
                                <CheckCircle className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
            
            {/* Questions Column */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col">
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-xl font-bold text-gray-800 flex items-center">
                    <HelpCircle className="h-5 w-5 mr-2 text-indigo-600" />
                    Questions
                  </h2>
                  {selectedPassage && (
                    <button
                      onClick={() => setCreatingQuestion(true)}
                      className="px-3 py-1 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 transition-colors flex items-center"
                    >
                      <Plus className="mr-1 h-3 w-3" />
                      Add
                    </button>
                  )}
                </div>
                {selectedPassage ? (
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Search questions..."
                      value={questionSearch}
                      onChange={(e) => setQuestionSearch(e.target.value)}
                    />
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm italic">Select a passage to view questions</p>
                )}
              </div>
              
              {/* Questions List - Scrollable */}
              <div className="overflow-y-auto flex-1">
                {!selectedPassage ? (
                  <div className="p-6 text-center text-gray-500">
                    No passage selected
                  </div>
                ) : questionsLoading ? (
                  <div className="p-6 text-center text-gray-500">Loading questions...</div>
                ) : questionsError ? (
                  <div className="p-6 text-center text-red-500">Error loading questions</div>
                ) : questions.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">
                    No questions found for this passage. Create a new question to get started.
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {questions.map((question) => (
                      <div 
                        key={question.id}
                        className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                          selectedQuestion?.id === question.id ? 'bg-indigo-50' : ''
                        }`}
                        onClick={() => {
                          if (selectedQuestion?.id === question.id) {
                            setSelectedQuestion(null);
                          } else {
                            setSelectedQuestion(question);
                          }
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center mb-2">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mr-2 ${
                                question?.approved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {question?.approved ? 'Approved' : 'Pending'}
                              </span>
                              {question.level && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  {question.level}
                                </span>
                              )}
                              {question.answer_id ? (
                                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  Has Answer
                                </span>
                              ) : (
                                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  No Answer
                                </span>
                              )}
                            </div>
                            <div className="flex items-center">
                              <ChevronRight className={`h-5 w-5 mr-2 text-gray-400 transition-transform ${
                                selectedQuestion?.id === question.id ? 'transform rotate-90' : ''
                              }`} />
                              <h3 className="text-sm font-medium text-gray-900">
                                {question.prompt.length > 50 && selectedQuestion?.id !== question.id
                                  ? replaceTaggedValues(question.prompt.substring(0, 50) + '...') 
                                  : replaceTaggedValues(question.prompt)}
                              </h3>
                            </div>
                          </div>
                          <div className="flex items-center space-x-1 ml-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingQuestion({...question});
                              }}
                              className="p-1 text-gray-500 hover:text-indigo-600 transition-colors"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            {/* <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (confirm('Are you sure you want to delete this question? This will also delete all associated answers.')) {
                                  deleteQuestionMutation.mutate(question.id);
                                }
                              }}
                              className="p-1 text-gray-500 hover:text-red-600 transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button> */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                updateQuestionMutation.mutate({
                                  ...question,
                                  approved: !question?.approved
                                });
                              }}
                              className={`p-1 text-gray-500 transition-colors`}
                              // className={`p-1 ${
                              //   question?.approved 
                              //     ? 'text-green-500 hover:text-gray-500' 
                              //     : 'text-gray-500 hover:text-green-500'
                              // } transition-colors`}
                            >
                              {question?.approved ? (
                                <XCircle className="h-4 w-4" />
                              ) : (
                                <CheckCircle className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* Answers Column */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col">
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-xl font-bold text-gray-800 flex items-center">
                    <List className="h-5 w-5 mr-2 text-indigo-600" />
                    Answers
                  </h2>
                  {selectedQuestion && (
                    <button
                      onClick={() => setCreatingAnswer(true)}
                      className="px-3 py-1 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 transition-colors flex items-center"
                    >
                      <Plus className="mr-1 h-3 w-3" />
                      Add
                    </button>
                  )}
                </div>
                {selectedQuestion ? (
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Search answers..."
                      value={answerSearch}
                      onChange={(e) => setAnswerSearch(e.target.value)}
                    />
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm italic">Select a question to view answers</p>
                )}
              </div>
              
              {/* Answers List with Drag & Drop - Scrollable */}
              <div className="overflow-y-auto flex-1">
                {!selectedQuestion ? (
                  <div className="p-6 text-center text-gray-500">
                    No question selected
                  </div>
                ) : answersLoading ? (
                  <div className="p-6 text-center text-gray-500">Loading answers...</div>
                ) : answersError ? (
                  <div className="p-6 text-center text-red-500">Error loading answers</div>
                ) : answers.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">
                    No answers found for this question. Create new answers to get started.
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                  {answers.map((answer) => (
                    <div 
                      key={answer.id}
                      className={`p-4 hover:bg-gray-50 transition-colors relative
                        ${selectedQuestion?.answer_id === answer.id ? 'bg-green-50' : ''}
                        ${dragOverAnswerId === answer.id ? 'border-t-2 border-indigo-500' : ''}
                        ${isDragging ? 'cursor-move' : ''}
                        ${draggedAnswerId === answer.id ? 'bg-indigo-50/50' : ''}`}
                      draggable={true}
                      onDragStart={(e) => handleAnswerDragStart(e, answer.id)}
                      onDragOver={(e) => handleAnswerDragOver(e, answer.id)}
                      onDragLeave={handleAnswerDragLeave}
                      onDrop={(e) => handleAnswerDrop(e, answer.id)}
                      onDragEnd={handleDragEnd}
                      data-answer-id={answer.id}
                      onTouchStart={(e) => handleTouchStart(e, answer.id)}
                      onTouchMove={(e) => handleTouchMove(e, answer.id)}
                      onTouchEnd={handleTouchEnd}
                      onTouchCancel={handleTouchEnd}
                    >
                      <div className="flex items-start">
                        <div 
                          className="mr-2 cursor-move text-gray-400 touch-manipulation"
                          onMouseDown={(e) => e.stopPropagation()}
                          onTouchStart={(e) => e.stopPropagation()}
                        >
                          <GripVertical className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center mb-2">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mr-2">
                              #{answer.sorted || 0}
                            </span>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mr-2 ${
                              answer.approved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {answer.approved ? 'Approved' : 'Pending'}
                            </span>
                            {selectedQuestion?.answer_id === answer.id && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Correct
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-900">
                            {replaceTaggedValues(answer.content)}</p>
                        </div>
                        <div className="flex items-center space-x-1 ml-2">
                          <button
                            onClick={() => setEditingAnswer({...answer})}
                            className="p-1 text-gray-500 hover:text-indigo-600 transition-colors"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => updateAnswerMutation.mutate({
                              ...answer,
                              approved: !answer.approved
                            })}
                            className="p-1 text-gray-500 transition-colors"
                          >
                            {answer.approved ? (
                              <XCircle className="h-4 w-4" />
                            ) : (
                              <CheckCircle className="h-4 w-4" />
                            )}
                          </button>
                          {selectedQuestion?.answer_id !== answer.id ? (
                            <button
                              onClick={() => updateQuestionMutation.mutate({
                                ...selectedQuestion,
                                answer_id: answer.id
                              })}
                              className="p-1 text-gray-500 hover:text-green-500 transition-colors"
                              title="Set as correct answer"
                            >
                              <SquareCheck className="h-4 w-4" />
                            </button>
                          ) : (
                            <Star className="text-yellow-500 h-4 w-4" />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {isDragging && (
                    <DragIndicator 
                      visible={isDragging} 
                      position={dragIndicatorPosition}
                    />
                  )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Empty State */}
      {!selectedPassage && passages.length === 0 && !creatingPassage && !passagesLoading && (
        <div className="max-w-7xl mx-auto mt-8">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <MessageSquare className="h-16 w-16 text-indigo-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">No Content Yet</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Get started by creating your first passage. Passages contain questions, which contain answers.
            </p>
            <button 
              onClick={() => setCreatingPassage(true)}
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors shadow-md flex items-center mx-auto"
            >
              <Plus className="mr-2 h-5 w-5" />
              Create Your First Passage
            </button>
          </div>
        </div>
      )}
      
      {/* Forms and Modals */}
      
      {/* Create/Edit Passage Modal */}
      {(editingPassage || creatingPassage) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl h-[calc(100vh-4rem)] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-800">
                  {editingPassage ? 'Edit Passage' : 'Create New Passage'}
                </h3>
                <button
                  onClick={() => {
                    setEditingPassage(null);
                    setCreatingPassage(false);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Question Type
                  </label>
                  <select
                    value={editingPassage?.question_type || emptyPassage?.question_type}
                    onChange={(e) => {
                      if (editingPassage) {
                        setEditingPassage({...editingPassage, question_type: e.target.value});
                      } else {
                        setEmptyPassage({...emptyPassage, question_type: e.target.value});
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="reading_comprehension">Reading Comprehension</option>
                    <option value="logic_reasoning">Logical Reasoning</option>
                    <option value="assumption">Assumption</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Level
                  </label>
                  <select
                    value={editingPassage?.level || emptyPassage?.level || ''}
                    onChange={(e) => {
                      const value = e.target.value || null;
                      if (editingPassage) {
                        setEditingPassage({...editingPassage, level: value});
                      } else {
                        setEmptyPassage({...emptyPassage, level: value});
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">None</option>
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="expert">Expert</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Approval Status
                  </label>
                  <div className="flex items-center space-x-4">
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        className="form-radio text-indigo-600"
                        checked={editingPassage ? editingPassage?.approved === true : emptyPassage?.approved === true}
                        onChange={() => {
                          if (editingPassage) {
                            setEditingPassage({...editingPassage, approved: true});
                          } else {
                            setEmptyPassage({...emptyPassage, approved: true});
                          }
                        }}
                      />
                      <span className="ml-2">Approved</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        className="form-radio text-indigo-600"
                        checked={editingPassage ? editingPassage?.approved === false : emptyPassage?.approved === false}
                        onChange={() => {
                          if (editingPassage) {
                            setEditingPassage({...editingPassage, approved: false});
                          } else {
                            setEmptyPassage({...emptyPassage, approved: false});
                          }
                        }}
                      />
                      <span className="ml-2">Unapproved</span>
                    </label>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prompt (Passage Text)
                  </label>
                  <TextEditor
                    content={editingPassage?.prompt || emptyPassage?.prompt}
                    onChange={(value) => {
                      if (editingPassage) {
                        setEditingPassage({...editingPassage, prompt: value});
                      } else {
                        setEmptyPassage({...emptyPassage, prompt: value});
                      }
                    }}
                  />
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setEditingPassage(null);
                  setCreatingPassage(false);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (editingPassage) {
                    updatePassageMutation.mutate(editingPassage);
                  } else {
                    createPassageMutation.mutate(emptyPassage);
                  }
                }}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors flex items-center"
                disabled={
                  editingPassage 
                    ? !editingPassage?.prompt || !editingPassage.question_type
                    : !emptyPassage?.prompt || !emptyPassage?.question_type
                }
              >
                <Save className="mr-2 h-4 w-4" />
                {editingPassage ? 'Update Passage' : 'Create Passage'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Create/Edit Question Modal */}
      {(editingQuestion || creatingQuestion) && selectedPassage && (
        <div className="overflow-y-auto fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-800">
                  {editingQuestion ? 'Edit Question' : 'Create New Question'}
                </h3>
                <button
                  onClick={() => {
                    setEditingQuestion(null);
                    setCreatingQuestion(false);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Level
                  </label>
                  <select
                    value={editingQuestion?.level || emptyQuestion?.level || ''}
                    onChange={(e) => {
                      const value = e.target.value || null;
                      if (editingQuestion) {
                        setEditingQuestion({...editingQuestion, level: value});
                      } else {
                        setEmptyQuestion({...emptyQuestion, level: value});
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">None</option>
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="expert">Expert</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Approval Status
                  </label>
                  <div className="flex items-center space-x-4">
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        className="form-radio text-indigo-600"
                        checked={editingQuestion ? editingQuestion?.approved === true : emptyQuestion?.approved === true}
                        onChange={() => {
                          if (editingQuestion) {
                            setEditingQuestion({...editingQuestion, approved: true});
                          } else {
                            setEmptyQuestion({...emptyQuestion, approved: true});
                          }
                        }}
                      />
                      <span className="ml-2">Approved</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        className="form-radio text-indigo-600"
                        checked={editingQuestion ? editingQuestion?.approved === false : emptyQuestion?.approved === false}
                        onChange={() => {
                          if (editingQuestion) {
                            setEditingQuestion({...editingQuestion, approved: false});
                          } else {
                            setEmptyQuestion({...emptyQuestion, approved: false});
                          }
                        }}
                      />
                      <span className="ml-2">Unapproved</span>
                    </label>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Question Text
                  </label>
                  <TextEditor
                    content={editingQuestion?.prompt || emptyQuestion?.prompt}
                    onChange={(value) => {
                      if (editingQuestion) {
                        setEditingQuestion({...editingQuestion, prompt: value});
                      } else {
                        setEmptyQuestion({...emptyQuestion, prompt: value});
                      }
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Explanation (Optional)
                  </label>
                  <textarea
                    value={editingQuestion?.explanation || emptyQuestion?.explanation || ''}
                    onChange={(e) => {
                      const value = e.target.value || null;
                      if (editingQuestion) {
                        setEditingQuestion({...editingQuestion, explanation: value});
                      } else {
                        setEmptyQuestion({...emptyQuestion, explanation: value});
                      }
                    }}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter an explanation for the correct answer..."
                  ></textarea>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hint (Optional)
                  </label>
                  <textarea
                    value={editingQuestion?.hint || emptyQuestion?.hint || ''}
                    onChange={(e) => {
                      const value = e.target.value || null;
                      if (editingQuestion) {
                        setEditingQuestion({...editingQuestion, hint: value});
                      } else {
                        setEmptyQuestion({...emptyQuestion, hint: value});
                      }
                    }}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter a hint for the question..."
                  ></textarea>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setEditingQuestion(null);
                  setCreatingQuestion(false);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (editingQuestion) {
                    updateQuestionMutation.mutate(editingQuestion);
                  } else {
                    createQuestionMutation.mutate({
                      ...emptyQuestion,
                      passage_id: selectedPassage.id
                    });
                  }
                }}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors flex items-center"
                disabled={
                  editingQuestion 
                    ? !editingQuestion.prompt
                    : !emptyQuestion?.prompt
                }
              >
                <Save className="mr-2 h-4 w-4" />
                {editingQuestion ? 'Update Question' : 'Create Question'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Create/Edit Answer Modal */}
      {(editingAnswer || creatingAnswer) && selectedQuestion && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-800">
                  {editingAnswer ? 'Edit Answer' : 'Create New Answer'}
                </h3>
                <button
                  onClick={() => {
                    setEditingAnswer(null);
                    setCreatingAnswer(false);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Answer Text
                  </label>
                  <TextEditor
                    content={editingAnswer?.content || emptyAnswer?.content}
                    onChange={(value) => {
                      if (editingAnswer) {
                        setEditingAnswer({...editingAnswer, content: value});
                      } else {
                        setEmptyAnswer({...emptyAnswer, content: value});
                      }
                    }}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sort Order
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={editingAnswer?.sorted || emptyAnswer?.sorted || 1}
                    onChange={(e) => {
                      const value = parseInt(e.target.value) || 1;
                      if (editingAnswer) {
                        setEditingAnswer({...editingAnswer, sorted: value});
                      } else {
                        setEmptyAnswer({...emptyAnswer, sorted: value});
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Approval Status
                  </label>
                  <div className="flex items-center space-x-4">
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        className="form-radio text-indigo-600"
                        checked={editingAnswer ? editingAnswer.approved === true : emptyAnswer?.approved === true}
                        onChange={() => {
                          if (editingAnswer) {
                            setEditingAnswer({...editingAnswer, approved: true});
                          } else {
                            setEmptyAnswer({...emptyAnswer, approved: true});
                          }
                        }}
                      />
                      <span className="ml-2">Approved</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        className="form-radio text-indigo-600"
                        checked={editingAnswer ? editingAnswer.approved === false : emptyAnswer?.approved === false}
                        onChange={() => {
                          if (editingAnswer) {
                            setEditingAnswer({...editingAnswer, approved: false});
                          } else {
                            setEmptyAnswer({...emptyAnswer, approved: false});
                          }
                        }}
                      />
                      <span className="ml-2">Unapproved</span>
                    </label>
                  </div>
                </div>
                
                <div className="pt-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      className="form-checkbox text-indigo-600"
                      checked={
                        editingAnswer 
                          ? selectedQuestion.answer_id === editingAnswer.id
                          : false
                      }
                      onChange={(e) => {
                        if (e.target.checked && editingAnswer) {
                          // Set this answer as correct
                          updateQuestionMutation.mutate({
                            ...selectedQuestion,
                            answer_id: editingAnswer.id
                          });
                        } else if (e.target.checked && !editingAnswer) {
                          // For new answers, we'll handle this after creation
                          alert('The answer will be set as correct after creation.');
                        } else if (editingAnswer && selectedQuestion.answer_id === editingAnswer.id) {
                          // Remove this answer as correct
                          updateQuestionMutation.mutate({
                            ...selectedQuestion,
                            answer_id: null
                          });
                        }
                      }}
                      disabled={!editingAnswer}
                    />
                    <span className="ml-2 text-sm text-gray-700">Set as correct answer</span>
                  </label>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setEditingAnswer(null);
                  setCreatingAnswer(false);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (editingAnswer) {
                    updateAnswerMutation.mutate(editingAnswer);
                  } else {
                    createAnswerMutation.mutate({
                      ...emptyAnswer,
                      question_id: selectedQuestion.id
                    });
                  }
                }}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors flex items-center"
                disabled={
                  editingAnswer
                    ? !editingAnswer.content
                    : !emptyAnswer?.content
                }
              >
                <Save className="mr-2 h-4 w-4" />
                {editingAnswer ? 'Update Answer' : 'Create Answer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPassagesPage;
